import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import { getOrderById, OrderDetailsResponse } from '../api/orders';
import { formatCurrency } from '../utils/pricing';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import ReorderButton from '../components/ReorderButton';

const OrderDetailsPage: React.FC = () => {
  return (
    <ProtectedRoute>
      <OrderDetails />
    </ProtectedRoute>
  );
};

const OrderDetails: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) return;
      
      try {
        setLoading(true);
        setError(null);
        const orderData = await getOrderById(orderId);
        
        // Security check: Verify the order belongs to the current user
        if (!orderData.email || 
            (orderData.email.toLowerCase() !== user?.email?.toLowerCase() && 
             orderData.customerId !== user?.ecwid_customer_id)) {
          setError("You don't have permission to view this order");
          return;
        }
        
        setOrder(orderData);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to fetch order details');
        console.error('Error fetching order details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, user]);

  const getFulfillmentStatusChip = (status: string) => {
    switch (status) {
      case 'DELIVERED':
        return <Chip label="Delivered" color="success" />;
      case 'SHIPPED':
        return <Chip label="Shipped" color="primary" />;
      case 'PROCESSING':
        return <Chip label="Processing" color="warning" />;
      case 'AWAITING_PROCESSING':
        return <Chip label="Awaiting Processing" color="default" />;
      default:
        return <Chip label={status} />;
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const formatPhone = (phone: string) => {
    return phone.replace(/(\+1)(\d{3})(\d{3})(\d{4})/, '$1 $2-$3-$4');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/orders')}>
          Back to Orders
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="warning">Order not found</Alert>
        <Button variant="contained" onClick={() => navigate('/orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Order #{order.orderNumber}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Placed on {formatDate(order.createDate)}
          </Typography>
          {order.orderComments && (
              <Typography variant="subtitle2" gutterBottom>
                Order Comments: {order.orderComments}
              </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ReorderButton 
            order={order} 
            variant="contained" 
          />
          <Button variant="outlined" onClick={() => navigate('/orders')}>
            Back to Orders
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' } }}>
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Items
            </Typography>
            <List>
              {order.items.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 && <Divider />}
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar 
                        variant="rounded" 
                        src={item.imageUrl} 
                        alt={item.name}
                        sx={{ width: 56, height: 56, mr: 2 }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.name}
                      secondary={
                        <React.Fragment>
                          <Typography variant="body2" component="span" display="block">
                            SKU: {item.sku}
                          </Typography>
                          <Typography variant="body2" component="span" display="block">
                            Quantity: {item.quantity} × {formatCurrency(item.price)}
                          </Typography>
                          {/* 
                              {item.attributes?.map((attr) => (
                              <Typography key={attr.name} variant="body2" component="span" display="block" color="text.secondary">
                                {attr.name}: {attr.value}
                              </Typography>
                            ))} 
                           */}
                        </React.Fragment>
                      }
                    />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      {formatCurrency(item.price * item.quantity)}
                    </Typography>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Shipping Information
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Shipping Method
              </Typography>
              <Typography variant="body1">
                {order.shippingOption?.shippingMethodName || 'No shipping method'}
                {order.shippingOption?.shippingRate ? 
                  ` (${formatCurrency(order.shippingOption.shippingRate)})` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {order.billingPerson && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Billing Address
                  </Typography>
                  <Typography variant="body1">
                    {order.billingPerson.name}<br />
                    {order.billingPerson.street}<br />
                    {order.billingPerson.city}, {order.billingPerson.stateOrProvinceName} {order.billingPerson.postalCode}<br />
                    {order.billingPerson.countryName}<br />
                    {formatPhone(order.billingPerson.phone)}
                  </Typography>
                </Box>
              )}
              {order.shippingPerson && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Shipping Address
                  </Typography>
                  <Typography variant="body1">
                    {order.shippingPerson.name}<br />
                    {order.shippingPerson.street}<br />
                    {order.shippingPerson.city}, {order.shippingPerson.stateOrProvinceName} {order.shippingPerson.postalCode}<br />
                    {order.shippingPerson.countryName}<br />
                    {formatPhone(order.shippingPerson.phone)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Status
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fulfillment Status
              </Typography>
              {getFulfillmentStatusChip(order.fulfillmentStatus)}
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Payment Method
              </Typography>
              <Typography variant="body1">{order.paymentMethod}</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal</Typography>
              <Typography variant="body1">{formatCurrency(order.subtotal)}</Typography>
            </Box>
            
            {/* Show individual taxes */}
            {order.items.reduce((taxes: any[], item) => {
              item.taxes.forEach(tax => {
                const existingTax = taxes.find(t => t.name === tax.name);
                if (existingTax) {
                  existingTax.total += tax.total;
                } else {
                  taxes.push({ name: tax.name, total: tax.total });
                }
              });
              return taxes;
            }, []).map(tax => (
              <Box key={tax.name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">{tax.name}</Typography>
                <Typography variant="body1">{formatCurrency(tax.total)}</Typography>
              </Box>
            ))}

            {/* Show shipping if applicable */}
            {order.shippingOption && order.shippingOption.shippingRate > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping</Typography>
                <Typography variant="body1">{formatCurrency(order.shippingOption.shippingRate)}</Typography>
              </Box>
            )}

            {/* Show custom surcharges */}
            {order.customSurcharges?.map(surcharge => (
              <Box key={surcharge.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">{surcharge.description}</Typography>
                <Typography variant="body1">{formatCurrency(surcharge.total)}</Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6">{formatCurrency(order.total)}</Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default OrderDetailsPage; 